import NotificationSubscriptions from '../api/notificationSubscription';
import auth from '../api/auth';

const pushError = code => Object.assign(new Error(code), { code });

export const withPushTimeout = (promise, milliseconds = 15000) =>
  new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(pushError('timeout')), milliseconds);
    Promise.resolve(promise).then(
      value => { clearTimeout(timer); resolve(value); },
      error => { clearTimeout(timer); reject(error); }
    );
  });

export const hasPushSupport = () =>
  'Notification' in window &&
  'serviceWorker' in navigator &&
  'PushManager' in window;

export const hasPushPermissions = () =>
  'Notification' in window && Notification.permission === 'granted';

export const getReadyPushRegistration = async () => {
  if (!hasPushSupport()) throw pushError('unsupported');
  const existing = await withPushTimeout(navigator.serviceWorker.getRegistration('/'));
  if (!existing) await withPushTimeout(navigator.serviceWorker.register('/sw.js'));
  // A newly registered worker is not necessarily active yet (notably on iOS).
  return withPushTimeout(navigator.serviceWorker.ready);
};

export const verifyServiceWorkerExistence = (callback = () => {}) => {
  if (!hasPushSupport()) return Promise.resolve(null);
  return getReadyPushRegistration().then(callback).catch(() => null);
};

const generateKeys = str =>
  btoa(String.fromCharCode.apply(null, new Uint8Array(str)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_');

export const getPushSubscriptionPayload = subscription => ({
  subscription_type: 'browser_push',
  subscription_attributes: {
    endpoint: subscription.endpoint,
    p256dh: generateKeys(subscription.getKey('p256dh')),
    auth: generateKeys(subscription.getKey('auth')),
  },
});

export const sendRegistrationToServer = subscription => {
  if (!auth.hasAuthCookie()) throw pushError('session');
  return NotificationSubscriptions.create(getPushSubscriptionPayload(subscription));
};

export const getCurrentPushSubscription = async () => {
  if (!hasPushSupport() || !hasPushPermissions()) return null;
  const registration = await getReadyPushRegistration();
  return withPushTimeout(registration.pushManager.getSubscription());
};

export const registerSubscription = async (
  onSuccess = () => {},
  onError = () => {}
) => {
  try {
    if (!window.chatwootConfig.vapidPublicKey) throw pushError('configuration');
    const registration = await getReadyPushRegistration();
    const existing = await withPushTimeout(registration.pushManager.getSubscription());
    const subscription = existing || await withPushTimeout(
      registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: window.chatwootConfig.vapidPublicKey,
      })
    );
    await withPushTimeout(sendRegistrationToServer(subscription));
    if (!hasPushPermissions() || !(await getCurrentPushSubscription())) {
      throw pushError('subscription');
    }
    onSuccess();
    return true;
  } catch (error) {
    onError(error);
    return false;
  }
};

export const requestPushPermissions = async ({ onSuccess, onError = () => {} }) => {
  try {
    if (!hasPushSupport()) throw pushError('unsupported');
    if (Notification.permission === 'denied') throw pushError('denied');
    // Request permission directly in the tap handler, before any awaited work.
    // Use the Promise API: Safari must not depend on the legacy callback API.
    const permission = Notification.permission === 'granted'
      ? 'granted'
      : await withPushTimeout(Notification.requestPermission(), 60000);
    if (permission !== 'granted') throw pushError(permission === 'denied' ? 'denied' : 'dismissed');
    return registerSubscription(onSuccess, onError);
  } catch (error) {
    onError(error);
    return false;
  }
};

export const disablePushSubscription = async () => {
  const subscription = await getCurrentPushSubscription();
  if (subscription) {
    const removed = await withPushTimeout(subscription.unsubscribe());
    if (!removed || await getCurrentPushSubscription()) throw pushError('unsubscribe');
  }
};
